/* eslint-disable react/no-unknown-property */
import { FC, useState } from 'react';
import {
  Button,
  Card,
  Icon,
  Input,
  Select,
  SelectOption,
  Spacer,
  Stack,
  Tag,
  TextBody,
  TextCaption,
  TextOverline,
} from '@pypestream/design-system';

import { Loader } from '../loader';

import { useLivePype } from './use-live-pype';
import { useTestApi } from './use-test-api';
import { RunResults } from './run-results';
import type { TestChannel } from './select-test-channel';
import { buildEngagementApiUrl, buildLivePreviewUrl } from '../../utils';

import './test-surface.scss';

export interface TestSurfaceProps {
  /**
   * Every channel this solution can be tested against. Rendered as a picker
   * whenever there is more than one: Sandbox is the safe default but not
   * always the working one — a solution published to Live but not Sandbox
   * gives a widget that boots and then refuses input, with nothing on screen
   * to say why.
   */
  channels?: TestChannel[];
  onSelectChannel?: (appId: string) => void;
  /**
   * The channel to boot the widget against. Pype binds to a CHANNEL, not a
   * solution — `appId` is the channel id, exactly as the Pype Designer passes
   * it. Null while the caller is still resolving which channel to use.
   */
  appId: string | null;
  channelEnv: 'live' | 'sandbox';
  /** Shown when there is no channel to test against, e.g. an unpublished solution. */
  noChannelReason?: string;
}

/**
 * The Test surface: a live conversation on the left, the run and its evidence
 * on the right.
 *
 * Shared deliberately. It is reachable from two places — the Workflows Test
 * View tab and the Channel Settings Test tab — and the only thing that differs
 * is how each caller arrives at a channel. Everything below the `appId` prop is
 * identical, so it lives here once.
 *
 * The LEFT pane is real: it boots an actual Pype widget against the actual
 * channel, so what you are talking to is the deployed solution, not a mock.
 *
 * The RIGHT pane is structural. Its shape follows the approved design — run
 * prompt, per-turn pass/fail with the pipeline breakdown, errors, the action
 * node log, the payload as sent. The pipeline breakdown and the action node
 * log now have a real source — Langfuse traces, fetched via `/test/trace`
 * once a run completes (see `use-test-api.ts`) — but per-turn pass/fail still
 * does not: that verdict comes from the test runner's own evaluation, which
 * does not exist yet. Rather than render an invented verdict that looks
 * authoritative, it states what it is waiting for.
 */
export const TestSurface: FC<TestSurfaceProps> = ({
  appId,
  channelEnv,
  noChannelReason,
  channels = [],
  onSelectChannel,
}) => {
  const [prompt, setPrompt] = useState('');
  const { containerRef, status, error, loaderStatus, session } = useLivePype(
    appId,
    channelEnv
  );
  const { runState, message, result, run, traceState, trace, traceMessage } =
    useTestApi(appId ? `${appId}:${channelEnv}` : null);

  return (
    <div className="c-test-surface" test-id="ps-test-surface">
      <section
        className="c-test-surface__chat"
        aria-label="Live conversation"
        test-id="ps-test-surface-chat"
      >
        {channels.length > 1 && (
          <div className="c-test-surface__channel">
            <Select
              test-id="ps-test-surface-channel"
              variant="outlined"
              size="small"
              label="Channel"
              value={appId ?? ''}
              onChange={(e) =>
                onSelectChannel?.((e.target as { value?: string }).value ?? '')
              }
            >
              {channels.map((channel) => (
                <SelectOption key={channel.appId} value={channel.appId}>
                  {channel.name} · {channel.channelEnv}
                </SelectOption>
              ))}
            </Select>
          </div>
        )}

        {/* The Loader mounts <ps-pype> into this element. It must keep a
          definite height: `anchorTo: 'container'` applies size containment, and
          a percentage height against an auto-height ancestor resolves to 0. */}
        <div className="c-test-surface__widget" ref={containerRef} />

        {/* What it is actually talking to. A widget that boots but will not
          accept input looks identical whichever channel and stack it reached,
          so naming both turns the next failure into a fact instead of a
          guess. */}
        {appId && (
          <TextCaption
            className="c-test-surface__target"
            test-id="ps-test-surface-target"
            textAlign="center"
            variant="tertiary"
          >
            {channelEnv} ·{' '}
            {
              new URL(buildEngagementApiUrl(window.location.origin, channelEnv))
                .host
            }
            {loaderStatus ? ` · ${loaderStatus}` : ''}
            {/* The conversation the widget is actually having. It belongs on
              the diagnostic line rather than in the results header: it is an
              engineering identifier, and it is the key that correlates this
              conversation with its traces. */}
            {session ? ` · chat ${session.chatId}` : ''}
            {/* A RECOVERABLE error reaches the screen nowhere else: the
              full-pane error state below is reserved for the fatal case, and
              `status` stays `booting` until `messenger:ready`. A widget that
              reports a problem and never becomes ready therefore left a
              spinner turning forever with the reason only in the console.
              Here it is a diagnostic among diagnostics rather than an overlay
              that flashes and vanishes when the boot recovers. */}
            {status !== 'error' && error ? ` · ${error}` : ''}
            {' · '}
            {/* The same channel in Pypestream's own preview harness, outside
              Pro Studio and outside this embed. If a conversation misbehaves
              here, this link says in one click whether the cause is the
              embedding or the solution itself. */}
            <a
              className="c-test-surface__target-link"
              test-id="ps-test-surface-real-preview"
              href={buildLivePreviewUrl(
                window.location.origin,
                channelEnv,
                appId
              )}
              target="_blank"
              rel="noreferrer"
            >
              open outside Pro Studio
            </a>
          </TextCaption>
        )}

        {!appId && (
          <div className="c-test-surface__chat-state">
            <TextBody size="small" variant="secondary" textAlign="center">
              {noChannelReason ?? 'No channel available to test against.'}
            </TextBody>
          </div>
        )}
        {appId && status === 'booting' && (
          <div className="c-test-surface__chat-state">
            <Loader relative />
          </div>
        )}
        {/* Only the FATAL case covers the pane — the loader never mounted, so
          there is nothing underneath to hide. A widget that started and then
          hit a recoverable problem renders its own unavailable state in
          place; a second overlay would sit on top of the real one. */}
        {appId && status === 'error' && (
          <div className="c-test-surface__chat-state">
            <TextBody size="small" textAlign="center">
              {error ?? 'The widget failed to start.'}
            </TextBody>
          </div>
        )}
      </section>

      <section
        className="c-test-surface__run"
        aria-label="Test run"
        test-id="ps-test-surface-run"
      >
        <Stack
          className="c-test-surface__prompt"
          gutter="small"
          alignItems="center"
          nowrap
        >
          <Input
            className="c-test-surface__prompt-input"
            test-id="ps-test-surface-prompt"
            variant="outlined"
            type="text"
            value={prompt}
            placeholder="Describe a conversation to run, in plain English"
            aria-label="Describe a conversation to run"
            // `onInput`, not `onChange`: the underlying control is a Lit web
            // component, and this is how every other Input in Pro Studio is
            // driven.
            // @ts-expect-error the wrapper types the event as the host element
            onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPrompt(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent) => {
              // The design puts Run beside the field; Enter is what anyone
              // typing into a single-line box will reach for first.
              if (e.key !== 'Enter' || !appId || !prompt.trim()) {
                return;
              }
              if (runState === 'running') return;
              run({ appId, channelEnv, prompt: prompt.trim() });
            }}
          />
          {/* Enabled by a channel and a prompt, and nothing else. It used to
            wait on the server's `capabilities.run`, which was
            `isLocalHost(host)` — so the button was permanently dead on every
            deployed build while working locally. The runner now runs HERE, in
            the page, so there is no capability to ask about.

            The design shows a black button; this is the design system's
            primary (purple), which is what Pro Studio uses for a primary
            action everywhere else. A one-off colour here would be the same
            drift that was corrected on the property editor. */}
          <Button
            test-id="ps-test-surface-run-btn"
            variant="primary"
            disabled={!appId || !prompt.trim() || runState === 'running'}
            onClick={() =>
              appId && run({ appId, channelEnv, prompt: prompt.trim() })
            }
          >
            <Icon slot="prefix" name="play" size="xsmall" />
            {runState === 'running' ? 'Running…' : 'Run'}
          </Button>
        </Stack>

        {/* The design's ERRORS block. Only a real failure fills it — a run
          that completed has nothing to report here, and an empty red panel
          would read as one that did. */}
        {runState === 'error' && message && (
          <div test-id="ps-test-surface-errors">
            <TextOverline>Errors</TextOverline>
            <Spacer size="2xsmall" />
            {/* `border="red"` is the design system's own error card. The
              mockup's pale pink fill has no token behind it, and the
              lightest red in the scale is heavy enough to push red text
              below a readable contrast. */}
            <Card border="red" size="small" shadow="none">
              <Stack gutter="small" alignItems="start" nowrap>
                <Tag variant="error" size="small">
                  <Icon name="close" size="xsmall" />
                  Fail
                </Tag>
                <TextBody size="small">{message}</TextBody>
              </Stack>
            </Card>
          </div>
        )}

        {result && (
          <RunResults
            result={result}
            channelName={
              channels.find((channel) => channel.appId === appId)?.name
            }
            traceState={traceState}
            trace={trace}
            traceMessage={traceMessage}
          />
        )}

        {/* A completed run still has something to say — that nothing has
          judged the replies. Not an error, so not in the block above. */}
        {result && runState !== 'error' && message && (
          <TextCaption
            test-id="ps-test-surface-run-message"
            variant="secondary"
          >
            {message}
          </TextCaption>
        )}

        {/* Only while there is nothing to show. Once a run has happened, its
          transcript is the answer to "what goes here", and leaving a
          placeholder underneath it would suggest the real panel is still
          missing. */}
        {!result && (
          <Card
            variant="secondary"
            shadow="none"
            test-id="ps-test-surface-pending"
          >
            <Stack
              direction="column"
              gutter="2xsmall"
              alignItems="stretch"
              nowrap
            >
              <TextBody size="small">Run results will appear here</TextBody>
            </Stack>
          </Card>
        )}
      </section>
    </div>
  );
};
